import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useToast } from "../contexts/ToastContext";
import {
  createAcquisitionCost,
  deleteAcquisitionCost,
  getAcquisitionCosts,
  getUnitEconomics,
  getUnitEconomicsCohorts,
  updateAcquisitionCost,
} from "../services/unitEconomicsApi";
import { AcquisitionCostInput } from "../types/unit-economics.types";

export const UNIT_ECONOMICS_KEYS = {
  all: ["kiedynabory-unit-economics"] as const,
  metrics: (from: string, to: string) =>
    [...UNIT_ECONOMICS_KEYS.all, "metrics", from, to] as const,
  cohorts: (from: string, to: string) =>
    [...UNIT_ECONOMICS_KEYS.all, "cohorts", from, to] as const,
  acquisitionCosts: () =>
    [...UNIT_ECONOMICS_KEYS.all, "acquisition-costs"] as const,
};

/**
 * Fetch unit economics metrics for the given period (current + previous + trend).
 */
export const useUnitEconomicsQuery = (params: { from: string; to: string }) => {
  return useQuery({
    queryKey: UNIT_ECONOMICS_KEYS.metrics(params.from, params.to),
    queryFn: () => getUnitEconomics(params),
    enabled: !!params.from && !!params.to,
    staleTime: 1000 * 60 * 5, // 5 minutes (matches backend cache TTL)
  });
};

/**
 * Fetch cohort-LTV data for the given period. Independent of the metrics query
 * so the Kohorty tab has its own loading / error / empty states (C3).
 */
export const useUnitEconomicsCohortsQuery = (params: {
  from: string;
  to: string;
  enabled?: boolean;
}) => {
  return useQuery({
    queryKey: UNIT_ECONOMICS_KEYS.cohorts(params.from, params.to),
    queryFn: () => getUnitEconomicsCohorts(params),
    enabled: (params.enabled ?? true) && !!params.from && !!params.to,
    staleTime: 1000 * 60 * 5,
  });
};

/**
 * Fetch the list of acquisition cost entries.
 */
export const useAcquisitionCostsQuery = () => {
  return useQuery({
    queryKey: UNIT_ECONOMICS_KEYS.acquisitionCosts(),
    queryFn: getAcquisitionCosts,
    staleTime: 1000 * 60 * 5,
  });
};

const invalidateAll = (queryClient: ReturnType<typeof useQueryClient>) => {
  queryClient.invalidateQueries({ queryKey: UNIT_ECONOMICS_KEYS.all });
};

/** Extracts a human-readable message from an AxiosError, falling back otherwise. */
export const extractErrorMessage = (error: unknown, fallback: string): string => {
  const data =
    error instanceof AxiosError
      ? (error.response?.data as { message?: string } | undefined)
      : undefined;
  return data?.message ?? fallback;
};

/**
 * Create an acquisition cost. Admin-only.
 */
export const useCreateAcquisitionCostMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (input: AcquisitionCostInput) => createAcquisitionCost(input),
    onSuccess: () => {
      invalidateAll(queryClient);
      addToast({ message: "Koszt został dodany", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Błąd dodawania kosztu"),
        type: "error",
      });
    },
  });
};

/**
 * Update an acquisition cost. Admin-only.
 */
export const useUpdateAcquisitionCostMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AcquisitionCostInput }) =>
      updateAcquisitionCost(id, input),
    onSuccess: () => {
      invalidateAll(queryClient);
      addToast({ message: "Koszt został zaktualizowany", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Błąd aktualizacji kosztu"),
        type: "error",
      });
    },
  });
};

/**
 * Delete an acquisition cost. Admin-only.
 */
export const useDeleteAcquisitionCostMutation = () => {
  const queryClient = useQueryClient();
  const { addToast } = useToast();

  return useMutation({
    mutationFn: (id: string) => deleteAcquisitionCost(id),
    onSuccess: () => {
      invalidateAll(queryClient);
      addToast({ message: "Koszt został usunięty", type: "success" });
    },
    onError: (error: unknown) => {
      addToast({
        message: extractErrorMessage(error, "Błąd usuwania kosztu"),
        type: "error",
      });
    },
  });
};
